import { useEffect, useMemo, useRef, useState } from 'react'

import { ApplyConfirmation } from '@/components/storefronts/ApplyConfirmation'
import { StorefrontModal } from '@/components/storefronts/StorefrontModal'
import { AutoRepriceList } from '@/components/views/dialogs/AutoRepriceList'
import { PricingPreviewDialog } from '@/components/ymarket-pricing/PricingPreviewDialog'
import { announceOperation, useWatchedOperation } from '@/hooks/useOperations'
import { useSelectedYmOffers } from '@/hooks/useSelectedYmOffers'
import { getInventory, type InventoryFilters } from '@/lib/api/client/inventory'
import { applyYmEconomicsBulk, previewYmEconomicsBulk } from '@/lib/api/client/ymarket-pricing'
import type { InventoryListItem } from '@/lib/api/schemas/inventory.schema'
import type { Operation } from '@/lib/api/schemas/operations.schema'
import type {
  YmEconomicsBulkChannel,
  YmEconomicsBulkMode,
  YmEconomicsBulkPreview,
  YmEconomicsBulkPreviewRequest,
  YmEconomicsBulkRow,
  YmMinimumForPromosMode,
  YmOfferPricing,
  YmPriceChange,
  YmPricingEarnings,
} from '@/lib/api/schemas/ymarket-pricing.schema'
import { formatRubleAmount, formatRublePerGram, pluralPieces } from '@/lib/market-money'
import type { SelectionScope } from '@/lib/views/actions'
import { unavailableReasonText } from '@/lib/warehouse/labels'
import { selectionFilterBody } from '@/lib/warehouse/selection-filter'
import { isActiveOperation } from '@/lib/operations/labels'
import { MINIMUM_MODE_LABELS } from '@/lib/ymarket-pricing'

type MarketplaceOption = {
  key: YmEconomicsBulkChannel | 'ozon' | 'ori_site'
  label: string
  enabled: boolean
}

/** «Не менять цену» restates the current price so only the struck-through price or the minimum moves. */
type RepriceMode = Exclude<YmEconomicsBulkMode, 'policy' | 'net_per_gram'> | 'keep'
type ValueMode = 'percent' | 'per_gram' | 'price'

function isBulkChannel(key: MarketplaceOption['key']): key is YmEconomicsBulkChannel {
  return key === 'ymarket' || key === 'wildberries'
}

function hasValue(mode: RepriceMode): mode is ValueMode {
  return mode === 'percent' || mode === 'per_gram' || mode === 'price'
}

const MARKETPLACE_OPTIONS: readonly MarketplaceOption[] = [
  { key: 'ymarket', label: 'Яндекс Маркет', enabled: true },
  { key: 'wildberries', label: 'Wildberries', enabled: true },
  { key: 'ozon', label: 'Ozon', enabled: false },
  { key: 'ori_site', label: 'ori', enabled: false },
]

const BULK_CHANNELS: readonly YmEconomicsBulkChannel[] = ['ymarket', 'wildberries']

const CHANNEL_LABELS: Record<YmEconomicsBulkChannel, string> = {
  ymarket: 'Яндекс Маркет',
  wildberries: 'Wildberries',
}

const MODE_OPTIONS: readonly { value: RepriceMode; label: string }[] = [
  { value: 'auto', label: 'Авто' },
  { value: 'percent', label: '±%' },
  { value: 'per_gram', label: 'Цена за грамм' },
  { value: 'price', label: 'Цена' },
  { value: 'keep', label: 'Не менять цену' },
]

const VALUE_LABELS: Record<ValueMode, string> = {
  percent: 'Изменение цены, %',
  per_gram: 'Цена за грамм, ₽/г',
  price: 'Цена, ₽',
}

// Each mode keeps its own value, so switching modes never rewrites another field.
const DEFAULT_VALUES: Record<ValueMode, string> = {
  percent: '',
  per_gram: '',
  price: '',
}

const MINIMUM_OPTIONS: readonly YmMinimumForPromosMode[] = ['keep', 'percent_of_price', 'price']

const MAX_ID_SELECTION = 500
const PERCENT_MIN = -90
const PERCENT_MAX = 300
const MARKUP_MAX = 1000
const PREVIEW_DEBOUNCE_MS = 400

function selectionCount(selection: SelectionScope<InventoryFilters>): number {
  return selection.kind === 'ids' ? selection.ids.length : selection.expectedTotal
}

function parsedNumber(value: string): number | null {
  if (value.trim() === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function listedOn(row: InventoryListItem, channel: YmEconomicsBulkChannel): boolean {
  if (channel === 'ymarket' && row.ymarketEconomics !== null && row.ymarketEconomics !== undefined) return true
  return (row.storefrontPrices ?? []).some((price) => price.target === channel && price.listed)
}

/** The channels the selected pieces are listed on, or `null` when only the server can tell. */
function knownChannels(selection: SelectionScope<InventoryFilters>, rows: readonly InventoryListItem[]): YmEconomicsBulkChannel[] | null {
  if (selection.kind !== 'ids') {
    const present = BULK_CHANNELS.filter((channel) => (selection.filter.presentOn ?? []).includes(channel))
    return present.length > 0 ? present : null
  }
  const byId = new Map(rows.map((row) => [row.productId, row]))
  const known = selection.ids.map((id) => byId.get(id)).filter((row): row is InventoryListItem => row !== undefined)
  if (known.length < selection.ids.length) return null
  return BULK_CHANNELS.filter((channel) => known.some((row) => listedOn(row, channel)))
}

/** Channels preselected only where the selected pieces are listed; a filter asks the server once. */
function useListedChannels(selection: SelectionScope<InventoryFilters>, rows: readonly InventoryListItem[]): YmEconomicsBulkChannel[] | null {
  const [initial] = useState(() => knownChannels(selection, rows))
  const [probed, setProbed] = useState<YmEconomicsBulkChannel[] | null>(null)
  useEffect(() => {
    if (initial !== null) return undefined
    if (selection.kind === 'ids') {
      setProbed([...BULK_CHANNELS])
      return undefined
    }
    const controller = new AbortController()
    Promise.all(BULK_CHANNELS.map((channel) => getInventory({ ...selection.filter, presentOn: [channel], limit: 1, offset: 0 }, controller.signal)
      .then((page) => (page.total > 0 ? channel : null))))
      .then((found) => { if (!controller.signal.aborted) setProbed(found.filter((channel): channel is YmEconomicsBulkChannel => channel !== null)) })
      .catch(() => { if (!controller.signal.aborted) setProbed([...BULK_CHANNELS]) })
    return () => controller.abort()
  }, [initial, selection])
  return initial ?? probed
}

/** The first reason the inputs cannot be previewed, in the order the owner fills the form. */
function validationReason(
  channels: readonly YmEconomicsBulkChannel[],
  mode: RepriceMode,
  value: number | null,
  markupText: string,
  markup: number | null,
  minimumMode: YmMinimumForPromosMode,
  minimum: number | null,
): string | null {
  if (channels.length === 0) return 'Выберите хотя бы одну площадку.'
  if (hasValue(mode)) {
    if (value === null) return 'Введите значение.'
    if (mode === 'percent' && value !== null && (value < PERCENT_MIN || value > PERCENT_MAX)) return 'Изменение в % — от −90 до 300.'
    if (mode !== 'percent' && value !== null && value <= 0) return 'Значение должно быть больше нуля.'
  }
  const markupAsked = channels.includes('ymarket') && mode !== 'auto'
  if (markupAsked && markupText.trim() !== '' && (markup === null || !Number.isInteger(markup) || markup < 1 || markup > MARKUP_MAX)) {
    return 'Зачёркнутая цена: целое число процентов от 1 до 1000.'
  }
  // The price-batch writer has no «keep the crossed price»; an empty field there would drop it.
  if (markupAsked && markup === null && (mode === 'percent' || mode === 'per_gram' || mode === 'price') && minimumMode === 'keep') {
    return 'Для этого способа укажите, на сколько % зачёркнутая цена Маркета выше новой.'
  }
  if (minimumMode !== 'keep' && (minimum === null || minimum < 0)) return 'Введите минимум для акций.'
  return null
}

function price(value: number | null): string {
  return value === null ? '—' : formatRubleAmount(value)
}

function pricePair(actual: number | null, strike: number | null) {
  return (
    <span className="flex min-w-[100px] flex-col tabular-nums">
      <span>{price(actual)}</span>
      {strike === null ? null : <span className="text-xs text-ink-muted line-through">{price(strike)}</span>}
    </span>
  )
}

function earningsText(earnings: YmPricingEarnings): string {
  if (earnings.earnings === null || earnings.earningsPercent === null) {
    return earnings.unavailableReason === null ? '—' : `— (${unavailableReasonText(earnings.unavailableReason)})`
  }
  return `${formatRubleAmount(earnings.earnings)} · ${earnings.earningsPercent.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} %`
}

function refusalText(row: YmEconomicsBulkRow): string {
  return row.refusals.map((refusal) => refusal.message).join('; ')
}

function previewRequest(
  selection: SelectionScope<InventoryFilters>,
  mode: Exclude<YmEconomicsBulkMode, 'policy' | 'net_per_gram'>,
  value: number | null,
  channels: readonly YmEconomicsBulkChannel[],
  markup: number | null,
  overrides?: Record<string, number>,
): YmEconomicsBulkPreviewRequest {
  const scope = selection.kind === 'ids'
    ? { productIds: [...selection.ids] }
    : { filter: selectionFilterBody(selection.filter), expectedTotal: selection.expectedTotal }
  return {
    ...scope,
    mode,
    ...(mode === 'auto' || value === null ? {} : { value }),
    channels: [...channels],
    ymarketStrikeMarkupPercent: mode === 'auto' ? null : markup,
    ...(mode === 'auto' && overrides !== undefined && Object.keys(overrides).length > 0 ? { overrides } : {}),
  }
}

function overridesKey(overrides: Readonly<Record<string, number>>): string {
  return JSON.stringify(Object.entries(overrides).sort(([left], [right]) => left.localeCompare(right)))
}

/** One YM pricing change per offer; `null` when «Не менять цену» meets an offer without any price yet. */
function ymarketChange(
  offer: YmOfferPricing,
  mode: 'price' | 'per_gram' | 'keep',
  value: number | null,
  markup: number | null,
  minimumMode: YmMinimumForPromosMode,
  minimum: number | null,
): YmPriceChange | null {
  const target = mode === 'keep' ? offer.forge.price ?? offer.ym?.price ?? null : value
  if (target === null || target <= 0) return null
  return {
    offerId: offer.offerId,
    mode: mode === 'keep' ? 'price' : mode,
    value: target,
    discountBase: markup === null ? { mode: 'keep', value: null } : { mode: 'markup_percent', value: markup },
    minimumForPromos: minimumMode === 'keep' ? { mode: 'keep', value: null } : { mode: minimumMode, value: minimum },
    promoParticipation: [],
    bandMode: false,
  }
}

export function RepriceDialog({
  selection,
  rows = [],
  onDone,
}: {
  selection: SelectionScope<InventoryFilters>
  rows?: readonly InventoryListItem[]
  onDone: () => void
}) {
  const doneRequested = useRef(false)
  const applyRequestedHash = useRef<string | null>(null)
  const finish = () => {
    if (doneRequested.current) return
    doneRequested.current = true
    onDone()
  }
  const [mode, setMode] = useState<RepriceMode>('auto')
  const autoMode = mode === 'auto'
  const listed = useListedChannels(selection, rows)
  const [chosenChannels, setChosenChannels] = useState<YmEconomicsBulkChannel[] | null>(null)
  const [values, setValues] = useState<Record<ValueMode, string>>(DEFAULT_VALUES)
  const [markupText, setMarkupText] = useState('')
  const [minimumMode, setMinimumMode] = useState<YmMinimumForPromosMode>('keep')
  const [minimumText, setMinimumText] = useState('')
  const [preview, setPreview] = useState<YmEconomicsBulkPreview | null>(null)
  const [operation, setOperation] = useState<Operation | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ymarketReview, setYmarketReview] = useState(false)
  const [previewRefresh, setPreviewRefresh] = useState(0)
  const [autoPriceTexts, setAutoPriceTexts] = useState<Record<string, string>>({})
  const [autoOverrides, setAutoOverrides] = useState<Record<string, number>>({})
  const [previewOverrideKey, setPreviewOverrideKey] = useState<string | null>(null)
  const [thresholdAcknowledged, setThresholdAcknowledged] = useState(false)
  const [refreshHandledId, setRefreshHandledId] = useState<string | null>(null)

  const channelChoice = chosenChannels ?? listed
  // Auto uses Market economics only; price editing remains available for both channels.
  const channels = useMemo<YmEconomicsBulkChannel[]>(
    () => autoMode ? ['ymarket'] : (channelChoice ?? []),
    [autoMode, channelChoice],
  )
  const channelsKnown = autoMode || channelChoice !== null
  const autoOverridesStamp = useMemo(() => overridesKey(autoOverrides), [autoOverrides])
  const watchedRefresh = useWatchedOperation(
    autoMode ? preview?.refreshOperationId ?? null : null,
  )
  const valueText = hasValue(mode) ? values[mode] : ''
  const value = parsedNumber(valueText)
  const markupShown = channels.includes('ymarket') && mode !== 'auto'
  const markup = markupShown ? parsedNumber(markupText) : null
  const minimum = parsedNumber(minimumText)
  const count = selectionCount(selection)
  const tooManyIds = selection.kind === 'ids' && selection.ids.length > MAX_ID_SELECTION
  const marketplaceNames = useMemo(
    () => channels.map((channel) => CHANNEL_LABELS[channel]).join(', '),
    [channels],
  )
  const minimumAvailable = channels.includes('ymarket') && (mode === 'price' || mode === 'per_gram' || mode === 'keep')
  const effectiveMinimumMode = minimumAvailable ? minimumMode : 'keep'
  // The price-batch writer carries price and struck-through price only; the minimum and a kept
  // price travel through the YM pricing writer, which knows those fields.
  const ymarketWriter = mode === 'keep' || (minimumAvailable && minimumMode !== 'keep')
  const ymarketWriterBlock = !ymarketWriter
    ? null
    : selection.kind === 'filter'
      ? 'Минимум для акций и «Не менять цену» меняются только для отмеченных изделий: выбор по фильтру здесь не поддерживается.'
      : channels.includes('wildberries') || !channels.includes('ymarket')
        ? 'Минимум для акций и «Не менять цену» есть только у Яндекс Маркета — оставьте одну площадку «Яндекс Маркет».'
        : null
  const selectedIds = ymarketWriter && ymarketWriterBlock === null && selection.kind === 'ids' ? selection.ids : null
  const offers = useSelectedYmOffers(selectedIds)
  const validation = channelsKnown
    ? validationReason(channels, mode, value, markupShown ? markupText : '', markup, effectiveMinimumMode, minimum)
    : null
  const shownValidation = tooManyIds || ymarketWriterBlock !== null ? null : validation

  const autoApplyRows = preview?.rows.filter(
    (row) => row.auto !== null
      && (row.newPrice !== null || row.refusals.some((refusal) => refusal.code === 'price_below_cost_floor'))
      && row.refusals.every((refusal) => refusal.code === 'price_below_cost_floor'),
  ) ?? []
  const autoThresholdPercent = autoApplyRows.find(
    (row) => row.auto?.confirmationThresholdPercent !== null,
  )?.auto?.confirmationThresholdPercent ?? null
  const autoThresholdExceeded = autoApplyRows.some((row) => {
    const raw = autoPriceTexts[row.productId] ?? String(row.newPrice ?? '')
    const next = raw.trim() === '' ? null : Number(raw)
    const threshold = row.auto?.confirmationThresholdPercent
    return row.currentPrice !== null && row.currentPrice > 0
      && next !== null && Number.isInteger(next) && next > 0
      && threshold !== null && threshold !== undefined
      && Math.abs((next / row.currentPrice - 1) * 100) > threshold
  })
  const autoPriceInvalid = autoApplyRows.some((row) => {
    const raw = autoPriceTexts[row.productId] ?? String(row.newPrice ?? '')
    const next = raw.trim() === '' ? null : Number(raw)
    return next === null || !Number.isInteger(next) || next <= 0
  })
  const autoPriceBelowFloor = autoApplyRows.some((row) => {
    const raw = autoPriceTexts[row.productId] ?? String(row.newPrice ?? '')
    const next = raw.trim() === '' ? null : Number(raw)
    return next !== null && Number.isInteger(next) && next > 0
      && row.auto?.costFloor !== null && row.auto?.costFloor !== undefined
      && next < row.auto.costFloor
  })
  const staleCollectId = autoMode ? preview?.refreshOperationId ?? null : null
  const refreshingPrices = staleCollectId !== null
    && (watchedRefresh === null || isActiveOperation(watchedRefresh))
  const staleRefreshMessage = refreshingPrices
    || (staleCollectId !== null && refreshHandledId === staleCollectId)
    ? 'Обновляем цены Маркета…'
    : preview?.refreshMessage !== null && preview?.refreshMessage !== undefined
      ? 'Не удалось обновить цены Маркета.'
      : null

  useEffect(() => {
    if (!autoMode) {
      setPreview(null)
      setPreviewOverrideKey(null)
    }
    setConfirming(false)
    if (!channelsKnown || mode === 'keep' || ymarketWriter) return undefined
    if (tooManyIds || validation !== null) return undefined
    const controller = new AbortController()
    setBusy(true)
    setError(null)
    // Typing a value fires one preview after the pause, not one per keystroke.
    const timer = window.setTimeout(() => {
      const requestOverrideKey = autoMode ? overridesKey(autoOverrides) : null
      previewYmEconomicsBulk(
        previewRequest(
          selection,
          mode,
          value,
          channels,
          markup,
          autoMode ? autoOverrides : undefined,
        ),
        controller.signal,
      )
        .then((result) => {
          if (!controller.signal.aborted) {
            setPreview(result)
            setPreviewOverrideKey(requestOverrideKey)
          }
        })
        .catch((reason: unknown) => {
          if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : 'Не удалось подготовить переоценку')
        })
        .finally(() => {
          if (!controller.signal.aborted) setBusy(false)
        })
    }, PREVIEW_DEBOUNCE_MS)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [autoMode, autoOverrides, channels, channelsKnown, markup, mode, previewRefresh, selection, tooManyIds, validation, value, ymarketWriter])

  useEffect(() => {
    if (preview?.mode !== 'auto') return
    setAutoPriceTexts((current) => {
      const next = { ...current }
      for (const row of preview.rows) {
        if (row.auto !== null && row.newPrice !== null && next[row.productId] === undefined) {
          next[row.productId] = String(row.newPrice)
        }
      }
      return next
    })
  }, [preview])

  useEffect(() => {
    if (
      staleCollectId === null
      || watchedRefresh === null
      || isActiveOperation(watchedRefresh)
      || refreshHandledId === staleCollectId
    ) return
    setRefreshHandledId(staleCollectId)
    setPreviewRefresh((current) => current + 1)
  }, [refreshHandledId, staleCollectId, watchedRefresh])

  const ymarketChanges = useMemo(() => {
    if (!ymarketWriter || offers.kind !== 'ready' || validation !== null) return null
    if (mode !== 'price' && mode !== 'per_gram' && mode !== 'keep') return null
    const changes = offers.offers
      .map((offer) => ymarketChange(offer, mode, value, markup, effectiveMinimumMode, minimum))
      .filter((change): change is YmPriceChange => change !== null)
    return { changes, skipped: offers.offers.length - changes.length }
  }, [effectiveMinimumMode, markup, minimum, mode, offers, validation, value, ymarketWriter])
  const nothingToChange = mode === 'keep' && effectiveMinimumMode === 'keep' && markup === null

  if (ymarketReview && ymarketChanges !== null && ymarketChanges.changes.length > 0 && offers.kind === 'ready') {
    return (
      <PricingPreviewDialog
        request={{ changes: ymarketChanges.changes }}
        offers={offers.offers}
        title={`Переоценить · ${pluralPieces(ymarketChanges.changes.length)}`}
        marketplaces="Яндекс Маркет"
        onClose={(applied) => (applied ? finish() : setYmarketReview(false))}
      />
    )
  }

  const changeableRows = preview?.rows.filter((row) => row.newPrice !== null && row.refusals.length === 0) ?? []
  const refusedRows = preview?.rows.filter((row) => row.refusals.length > 0
    && !row.refusals.every((refusal) => refusal.code === 'price_below_cost_floor')) ?? []
  const title = `Переоценить · ${pluralPieces(count)}`
  // A preview refresh never locks the inputs: disabling a field mid-typing drops keystrokes.
  const locked = applying || operation !== null
  const requiresExtraConfirmation = autoMode
    ? autoThresholdExceeded
    : preview?.rows.some((row) => row.auto?.requiresExtraConfirmation) === true
  const autoPreviewCurrent = previewOverrideKey === autoOverridesStamp
  const autoCanApply = preview !== null
    && preview.canApply
    && !preview.estimateOnly
    && autoApplyRows.length > 0
    && autoPreviewCurrent
    && !autoPriceInvalid
    && !autoPriceBelowFloor
    && (!autoThresholdExceeded || thresholdAcknowledged)
    && !refreshingPrices

  const toggleChannel = (channel: YmEconomicsBulkChannel) => {
    setChosenChannels((current) => {
      const base = current ?? channelChoice ?? []
      if (base.includes(channel)) return base.filter((candidate) => candidate !== channel)
      return BULK_CHANNELS.filter((candidate) => candidate === channel || base.includes(candidate))
    })
  }

  const requestApply = () => {
    if (preview === null || !preview.canApply || preview.estimateOnly || changeableRows.length === 0 || locked || busy || validation !== null) return
    if (autoMode && !autoCanApply) return
    setConfirming(true)
  }

  const confirmApply = async () => {
    if (!confirming || preview === null || !preview.canApply || preview.estimateOnly || changeableRows.length === 0 || locked || busy) return
    if (autoMode && !autoCanApply) return
    if (applyRequestedHash.current === preview.previewHash) return
    applyRequestedHash.current = preview.previewHash
    setApplying(true)
    setError(null)
    try {
      const queued = await applyYmEconomicsBulk(
        preview.previewHash,
        autoMode ? autoOverrides : undefined,
      )
      announceOperation(queued)
      setOperation(queued)
      setConfirming(false)
    } catch (reason: unknown) {
      applyRequestedHash.current = null
      setError(reason instanceof Error ? reason.message : 'Не удалось поставить переоценку в очередь')
    } finally {
      setApplying(false)
    }
  }

  const ymarketReady = ymarketChanges !== null && ymarketChanges.changes.length > 0 && !nothingToChange

  const changeAutoPrice = (productId: string, rawValue: string) => {
    setAutoPriceTexts((current) => ({ ...current, [productId]: rawValue }))
    setAutoOverrides((current) => {
      const price = rawValue.trim() === '' ? null : Number(rawValue)
      const next = { ...current }
      if (price === null || !Number.isInteger(price) || price <= 0) delete next[productId]
      else next[productId] = price
      return next
    })
    setThresholdAcknowledged(false)
  }

  return (
    <StorefrontModal title={title} labelledBy="reprice-title" wide busy={applying} stickyFooter={autoMode} onClose={finish} footer={(
      <>
        {shownValidation !== null ? <p id="reprice-validation" data-testid="reprice-validation" role="alert" className="mr-auto text-sm text-st-unpaid">{shownValidation}</p> : null}
        {autoMode && operation === null && autoThresholdExceeded && autoThresholdPercent !== null ? (
          <label className="order-first flex w-full items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={thresholdAcknowledged}
              disabled={busy || applying || locked}
              onChange={(event) => setThresholdAcknowledged(event.target.checked)}
            />
            Есть изменения больше {autoThresholdPercent} %
          </label>
        ) : null}
        {confirming && operation === null && !ymarketWriter ? (
          <ApplyConfirmation
            testId="reprice-confirm"
            acknowledgement={autoMode && requiresExtraConfirmation
              ? `Подтверждаю отправку новых цен на ${marketplaceNames} для ${pluralPieces(autoApplyRows.length)}`
              : `Отправить новые цены на ${marketplaceNames}`}
            busy={busy || applying}
            onConfirm={() => { void confirmApply() }}
            onCancel={() => setConfirming(false)}
          >
            {requiresExtraConfirmation ? 'Есть изменения выше установленного порога. Проверьте строки предпросмотра перед отправкой.' : `Переоценка на ${marketplaceNames}, ${pluralPieces(count)}. Запрос поставщику уйдёт только после второго подтверждения.`}
          </ApplyConfirmation>
        ) : null}
        {!autoMode ? <button type="button" className="btn btn-ghost" onClick={finish}>Закрыть</button> : null}
        {ymarketWriter ? (
          <button type="button" className="btn btn-primary" disabled={!ymarketReady || ymarketWriterBlock !== null || validation !== null} aria-describedby={shownValidation === null ? undefined : 'reprice-validation'} onClick={() => setYmarketReview(true)}>Показать предпросмотр</button>
        ) : operation !== null ? (
          <button type="button" className="btn btn-primary" onClick={finish}>Готово</button>
        ) : autoMode ? (
          <div className="ml-auto flex w-full flex-col items-end gap-1 sm:w-auto">
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || confirming || !autoCanApply || locked}
              onClick={requestApply}
            >
              Переоценить {pluralPieces(autoApplyRows.length)}
            </button>
          </div>
        ) : (
          <button type="button" className="btn btn-primary" disabled={busy || confirming || validation !== null || preview === null || !preview.canApply || preview.estimateOnly || changeableRows.length === 0} aria-describedby={shownValidation === null ? undefined : 'reprice-validation'} onClick={requestApply}>Применить</button>
        )}
      </>
    )}>
      <div className="space-y-4">
        {autoMode ? (
          <label className="inline-flex items-center gap-2 text-xs text-ink-muted">
            Режим переоценки
            <select
              className="input px-2 py-1 text-sm text-ink-2"
              value={mode}
              disabled={locked}
              aria-label="Режим переоценки"
              onChange={(event) => {
                const selected = MODE_OPTIONS.find((option) => option.value === event.target.value)
                if (selected !== undefined) setMode(selected.value)
              }}
            >
              {MODE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
        ) : null}
        {hasValue(mode) ? (
          <label className="block text-sm font-semibold">
            {VALUE_LABELS[mode]}
            <input
              data-autofocus
              className="input mt-1 block w-full"
              type="number"
              min={mode === 'percent' ? PERCENT_MIN : 0}
              max={mode === 'percent' ? PERCENT_MAX : undefined}
              step={mode === 'percent' ? '0.01' : '1'}
              value={valueText}
              disabled={locked}
              onChange={(event) => setValues((current) => ({ ...current, [mode]: event.target.value }))}
              aria-label={VALUE_LABELS[mode]}
            />
          </label>
        ) : mode === 'auto' ? null : (
          <p className="rounded-md bg-bone/60 px-3 py-2 text-sm text-ink-2">Цена остаётся прежней; меняются только зачёркнутая цена и минимум для акций на Яндекс Маркете.</p>
        )}
        {markupShown ? (
          <label className="flex items-center gap-2 text-sm font-semibold">
            Зачёркнутая цена: +
            <input className="input w-[90px]" type="number" min="1" max={MARKUP_MAX} step="1" placeholder="как сейчас" value={markupText} disabled={locked} onChange={(event) => setMarkupText(event.target.value)} aria-label="Зачёркнутая цена, % выше цены" />
            %
          </label>
        ) : null}

        {!autoMode ? <details className="rounded-lg border border-rule px-3 py-2 text-sm" data-testid="reprice-more">
          <summary className="cursor-pointer select-none font-semibold">Подробнее</summary>
          <div className="mt-3 space-y-4">
            <fieldset>
              <legend className="text-sm font-semibold">Куда переоценить</legend>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {MARKETPLACE_OPTIONS.map((option) => (
                  <label key={option.key} className={`rounded-lg border border-rule p-3 text-sm ${option.enabled ? 'cursor-pointer' : 'text-ink-muted'}`}>
                    <span className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={option.enabled && isBulkChannel(option.key) && channels.includes(option.key)}
                        disabled={!option.enabled || locked || !channelsKnown || (autoMode && option.key === 'wildberries')}
                        aria-label={option.enabled ? option.label : `${option.label} — Не подключено`}
                        onChange={() => { if (option.enabled && isBulkChannel(option.key)) toggleChannel(option.key) }}
                      />
                      <span className="font-medium">{option.label}</span>
                    </span>
                    {!option.enabled ? <span className="mt-1 block text-xs">Не подключено</span> : null}
                    {option.key === 'wildberries' && option.enabled ? <span className="mt-1 block text-xs text-ink-muted">{autoMode ? '«Авто» — только Яндекс Маркет' : 'Wildberries: только цена, без зачёркнутой'}</span> : null}
                  </label>
                ))}
              </div>
            </fieldset>
            <fieldset>
              <legend className="text-sm font-semibold">Как изменить цену</legend>
              <div className="mt-2 flex flex-wrap gap-4">
                {MODE_OPTIONS.map((option) => (
                  <label key={option.value} className="flex items-center gap-2 text-sm">
                    <input type="radio" name="reprice-mode" value={option.value} checked={mode === option.value} disabled={locked} onChange={() => setMode(option.value)} />
                    {option.label}
                  </label>
                ))}
              </div>
            </fieldset>
            {minimumAvailable ? (
              <fieldset>
                <legend className="text-sm font-semibold">Минимум для акций (Яндекс Маркет)</legend>
                <div className="mt-2 flex flex-wrap items-center gap-4">
                  {MINIMUM_OPTIONS.map((option) => (
                    <label key={option} className="flex items-center gap-2 text-sm">
                      <input type="radio" name="reprice-minimum" value={option} checked={minimumMode === option} disabled={locked} onChange={() => setMinimumMode(option)} />
                      {MINIMUM_MODE_LABELS[option]}
                    </label>
                  ))}
                  {minimumMode !== 'keep' ? (
                    <input className="input w-[140px]" type="number" min="0" step="1" value={minimumText} disabled={locked} onChange={(event) => setMinimumText(event.target.value)} aria-label="Значение минимума для акций" />
                  ) : null}
                </div>
              </fieldset>
            ) : null}
          </div>
        </details> : null}

        {!channelsKnown ? <p role="status" className="text-sm text-ink-muted">Проверяем, где выставлены изделия…</p> : null}
        {tooManyIds ? <p role="alert" className="rounded-md bg-red-50 p-3 text-sm text-st-unpaid">За один раз можно переоценить не более {MAX_ID_SELECTION} изделий.</p> : null}
        {ymarketWriterBlock !== null ? <p role="alert" className="rounded-md bg-red-50 p-3 text-sm text-st-unpaid">{ymarketWriterBlock}</p> : null}
        {ymarketWriter && ymarketWriterBlock === null ? (
          <div data-testid="reprice-ymarket-writer" className="space-y-1 rounded-md bg-bone/60 px-3 py-2 text-sm text-ink-2">
            <p>Предпросмотр и применение — через цены Яндекс Маркета: цена, зачёркнутая и минимум для акций одной операцией.</p>
            {offers.kind === 'loading' ? <p role="status" className="text-ink-muted">Ищем предложения на Маркете…</p> : null}
            {offers.kind === 'failed' ? <p role="alert" className="text-st-unpaid">{offers.message}</p> : null}
            {offers.kind === 'ready' ? <p className="tab-nums">Предложений на Маркете: {offers.offers.length} из {count}{ymarketChanges !== null && ymarketChanges.skipped > 0 ? ` · без текущей цены: ${ymarketChanges.skipped} — не изменятся` : ''}</p> : null}
            {nothingToChange ? <p className="text-ink-muted">Нечего менять: задайте зачёркнутую цену или минимум для акций.</p> : null}
          </div>
        ) : null}
        {busy && preview === null ? <p role="status" className="text-sm text-ink-muted">Готовим предпросмотр…</p> : null}
        {error ? <p role="alert" className="rounded-md bg-red-50 p-3 text-sm text-st-unpaid">{error}</p> : null}
        {operation !== null ? (
          <p role="status" className="rounded-md border border-received/40 bg-received-wash p-3 text-sm">
            Операция «{operation.title}» поставлена в очередь. Ход выполнения и результат по каждому изделию — в окне «Операции».
          </p>
        ) : null}
        {preview && !ymarketWriter ? autoMode ? (
          <>
            {staleRefreshMessage ? (
              <p role={preview?.refreshOperationId ? 'status' : 'alert'} className="text-xs text-ink-muted">
                {staleRefreshMessage}
              </p>
            ) : null}
            <AutoRepriceList
              rows={autoApplyRows}
              refusedRows={refusedRows}
              inventoryRows={rows}
              prices={autoPriceTexts}
              disabled={locked}
              onPriceChange={changeAutoPrice}
            />
          </>
        ) : (
          <>
            <p className="rounded-md bg-bone/60 px-3 py-2 text-sm font-semibold">Изменится: {changeableRows.length} · Отказано: {refusedRows.length}{preview.estimateOnly ? ' · только оценка' : ''}</p>
            {preview.promoAutoEnrolWarning ? <p role="status" className="rounded-md bg-bone/60 px-3 py-2 text-sm">{preview.promoAutoEnrolWarning}</p> : null}
            {requiresExtraConfirmation ? <p role="status" className="rounded-md border border-rule p-3 text-sm">Перед применением отдельно подтвердите изменение более {preview.rows.find((row) => row.auto?.requiresExtraConfirmation)?.auto?.confirmationThresholdPercent ?? 30} %.</p> : null}
            <div className="overflow-x-auto rounded-lg border border-rule">
              <table className="min-w-full text-left text-xs">
                <thead className="bg-bone/60 text-ink-2">
                  {
                    <tr>
                      <th className="px-3 py-2 font-semibold">Изделие</th>
                      <th className="px-3 py-2 font-semibold">Площадка</th>
                      <th className="px-3 py-2 font-semibold">Сейчас</th>
                      <th className="px-3 py-2 font-semibold">Станет</th>
                      <th className="px-3 py-2 font-semibold">Разница</th>
                      <th className="px-3 py-2 font-semibold">₽/г</th>
                      <th className="px-3 py-2 font-semibold">Заработок</th>
                    </tr>
                  }
                </thead>
                <tbody>
                  {preview.rows.map((row) => {
                    const refused = row.refusals.length > 0
                    return (
                      <tr key={`${row.productId}:${row.channel}`} className="border-t border-rule align-top" data-refused={refused ? 'true' : undefined}>
                        <td className="px-3 py-2 font-medium">{row.label}</td>
                        <td className="px-3 py-2">{CHANNEL_LABELS[row.channel]}</td>
                        <td className="px-3 py-2">{pricePair(row.currentPrice, row.currentOldPrice)}</td>
                        {refused ? (
                          <td colSpan={4} className="px-3 py-2 text-st-unpaid" data-testid="reprice-refusal">{refusalText(row)}</td>
                        ) : (
                          <>
                            <td className="px-3 py-2">{pricePair(row.newPrice, row.newOldPrice)}</td>
                            <td className="px-3 py-2 tabular-nums">{row.difference === null ? '—' : `${row.difference > 0 ? '+' : ''}${formatRubleAmount(row.difference)}`}</td>
                            <td className="px-3 py-2 tabular-nums">{formatRublePerGram(row.beforePricePerGram)} → {formatRublePerGram(row.afterPricePerGram)}</td>
                            <td className="px-3 py-2 tabular-nums">{earningsText(row.beforeEarnings)} → {earningsText(row.afterEarnings)}</td>
                          </>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
    </StorefrontModal>
  )
}
